import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
// next
import Script from 'next/script';
// @mui
import { Box } from '@mui/material';
// sentry
import * as Sentry from '@sentry/nextjs';

// ----------------------------------------------------------------------

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

// Managed 모드에서 체크박스가 나타났을 때의 위젯 높이. 미리 확보해 레이아웃이 밀리지 않게 한다.
const WIDGET_HEIGHT = 65;

// window.turnstile 준비 대기. 스크립트가 끝내 로드되지 않으면 onError 로 넘긴다.
const POLL_INTERVAL = 100;
const POLL_TIMEOUT = 10000;

// 위젯을 못 쓰게 된 원인. Sentry 에서 이 태그로 실제 발생 빈도를 집계한다.
const REASON = {
  NO_SITE_KEY: 'no_site_key',
  SCRIPT_LOAD_ERROR: 'script_load_error',
  SCRIPT_TIMEOUT: 'script_timeout',
  CHALLENGE_ERROR: 'challenge_error',
};

// ----------------------------------------------------------------------

const Turnstile = forwardRef(function Turnstile({ onVerify, onExpire, onError }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const failedRef = useRef(false);

  // 콜백은 ref 로 넘긴다. 부모가 리렌더될 때마다 위젯을 다시 그리면 안 되므로 render 는 마운트 시 한 번만 한다.
  const handlersRef = useRef();
  handlersRef.current = { onVerify, onExpire, onError };

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // 실패는 한 번만 보고한다. 스크립트 onError 와 폴링 타임아웃이 겹쳐 두 번 올라가는 것을 막는다.
  const reportFailure = useCallback(
    (reason, code) => {
      stopPolling();
      if (failedRef.current) return;
      failedRef.current = true;
      Sentry.captureMessage(`turnstile unavailable: ${reason}`, {
        level: 'warning',
        tags: { turnstile_reason: reason },
        extra: { code },
      });
      handlersRef.current.onError();
    },
    [stopPolling]
  );

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    []
  );

  const renderWidget = useCallback(() => {
    if (!SITE_KEY || widgetIdRef.current !== null || !containerRef.current || !window.turnstile) return;
    stopPolling();
    // 암묵적 렌더(class="cf-turnstile")는 스크립트 로드 시점에 한 번만 DOM 을 훑기 때문에
    // SPA 로 가입 페이지에 다시 들어오면 위젯이 뜨지 않는다. 그래서 explicit 렌더를 쓴다.
    // render 는 실패 시 undefined 를 반환하므로 null 로 맞춰둔다 (remove 대상 판별에 쓰인다).
    widgetIdRef.current =
      window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => {
          // 재시도로 복구된 경우. 다음 실패도 다시 보고되도록 플래그를 푼다.
          failedRef.current = false;
          handlersRef.current.onVerify(token);
        },
        'expired-callback': () => handlersRef.current.onExpire(),
        'error-callback': (code) => reportFailure(REASON.CHALLENGE_ERROR, code),
      }) ?? null;
  }, [reportFailure, stopPolling]);

  useEffect(() => {
    if (!SITE_KEY) {
      reportFailure(REASON.NO_SITE_KEY);
      return undefined;
    }

    let waited = 0;

    if (window.turnstile) {
      renderWidget();
    } else {
      pollTimerRef.current = setInterval(() => {
        if (window.turnstile) {
          renderWidget();
          return;
        }
        waited += POLL_INTERVAL;
        if (waited >= POLL_TIMEOUT) {
          reportFailure(REASON.SCRIPT_TIMEOUT);
        }
      }, POLL_INTERVAL);
    }

    return () => {
      stopPolling();
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [renderWidget, reportFailure, stopPolling]);

  return (
    <>
      {/* 폴링 타임아웃(10초) 뒤에 스크립트가 도착하는 저속 회선에서도 위젯이 뜨도록 onLoad 로 한 번 더 건다 */}
      {!!SITE_KEY && (
        <Script
          src={SCRIPT_SRC}
          strategy="afterInteractive"
          onLoad={renderWidget}
          onError={() => reportFailure(REASON.SCRIPT_LOAD_ERROR)}
        />
      )}
      <Box ref={containerRef} sx={{ minHeight: WIDGET_HEIGHT }} />
    </>
  );
});

Turnstile.propTypes = {
  onVerify: PropTypes.func.isRequired,
  onExpire: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default Turnstile;
